import { useState } from 'react';
import { Download, Share, Plus } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { usePWAInstall } from '@/hooks/usePWAInstall';

/**
 * Botón "Instalar aplicación" para el sidebar.
 * - Chrome/Edge/Android: lanza el prompt nativo de instalación.
 * - iOS (Safari): muestra instrucciones para "Agregar a inicio".
 * - Si ya está instalada, no se muestra.
 */
export default function InstallPWAButton() {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  // No mostrar si ya está instalada o si el navegador no permite instalar.
  if (isInstalled || (!canInstall && !isIOS)) return null;

  const handleClick = async () => {
    if (canInstall) {
      // Tras aceptar, el evento `appinstalled` oculta el botón automáticamente.
      await promptInstall();
      return;
    }
    if (isIOS) setShowIOSHelp(true);
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="flex w-full items-center px-3 py-2 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
      >
        <Download className="h-5 w-5 mr-3 flex-shrink-0" />
        Instalar aplicación
      </button>

      <Modal
        isOpen={showIOSHelp}
        onClose={() => setShowIOSHelp(false)}
        title="Instalar en tu iPhone / iPad"
        size="sm"
      >
        <div className="space-y-4 text-sm text-gray-700">
          <p>Para guardar ANIMAvet como aplicación en tu dispositivo Apple:</p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center">1</span>
              <span>
                Tocá el botón <strong>Compartir</strong>{' '}
                <Share className="inline h-4 w-4 -mt-0.5" /> en la barra de Safari.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center">2</span>
              <span>
                Elegí <strong>Agregar a inicio</strong>{' '}
                <Plus className="inline h-4 w-4 -mt-0.5" />.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center">3</span>
              <span>Confirmá tocando <strong>Agregar</strong>. ¡Listo!</span>
            </li>
          </ol>
          <p className="text-xs text-gray-500">
            Nota: en iPhone/iPad la instalación solo funciona desde Safari.
          </p>
        </div>
      </Modal>
    </>
  );
}
